import type { AxiosResponse } from "axios";

import { HttpApiService } from "@/shared/services/httpApi.service";

/** Active PSE bank as served by `GET /internal/payments/pse-banks` (PseBankDto). */
export interface PseBank {
  /** Participant code the initiate payload sends as `payer.bank_code`. */
  code: string;
  name: string;
}

/**
 * Active-bank directory for PSE payins. The list comes from the corridor provider's directory (rail-named
 * endpoint — the API hides which PSP serves it) and feeds the bank selector of the PSE deposit form; the
 * chosen `code` goes verbatim into `payer.bank_code` at initiation.
 */
export class PseBanksService {
  private static BASE_URL = "/internal/payments/pse-banks";

  static async list(): Promise<AxiosResponse<PseBank[]>> {
    return HttpApiService.get<PseBank[]>(this.BASE_URL);
  }
}

// ─── Directory memo ───────────────────────────────────────────────────────────
// The directory is the same ~30 rows for everyone and the network publishes it once a day, so re-fetching it
// every time a form mounts is pure waste. A session-scoped window keeps the deposit modal, the payins form and
// the return page on a single request. Kept out of the React hook on purpose: cache policy is plain logic and
// is worth testing without a DOM.

const STALE_AFTER_MS = 10 * 60_000;

let cache: { banks: PseBank[]; fetchedAt: number } | null = null;
let inflight: Promise<PseBank[]> | null = null;

/**
 * The bank directory, fetched at most once per window.
 *
 * Concurrent callers share one request (two surfaces mounting in the same tick must not issue two GETs), and
 * a failed request is never cached — the next call retries. `force` bypasses the memo for an explicit retry.
 */
export function ensurePseBanks(force = false): Promise<PseBank[]> {
  if (!force) {
    if (cache && Date.now() - cache.fetchedAt < STALE_AFTER_MS) return Promise.resolve(cache.banks);
    if (inflight) return inflight;
  }

  const request = PseBanksService.list()
    .then((response) => {
      const banks = response.data ?? [];
      cache = { banks, fetchedAt: Date.now() };
      return banks;
    })
    .finally(() => {
      if (inflight === request) inflight = null;
    });
  inflight = request;
  return request;
}

/** Resolves a bank's display name from a directory page — used where only the code was persisted. */
export function pseBankNameFor(banks: PseBank[] | null, code: string): string {
  return banks?.find((bank) => bank.code === code)?.name ?? "";
}
